import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import NewHomePage from './pages/NewHomePage';
import RichHomePage from './pages/RichHomePage';
import LecturePage from './pages/LecturePage';
import EventPage from './pages/EventPage';
import EventsPage from './pages/EventsPage';
import SpeakerPage from './pages/SpeakerPage';
import AboutPage from './pages/AboutPage';
import SupportPage from './pages/SupportPage';
import PublicationsPage from './pages/PublicationsPage';
import DebugPage from './pages/DebugPage';
import NotFoundPage from './pages/NotFoundPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CalendarPage from './pages/CalendarPage';
import CommunityServicesPage from './pages/CommunityServicesPage';
import ShortsViewer from './components/Shorts/ShortsViewer';
import VideosPage from './pages/VideosPage';
import VideoWatchPage from './pages/VideoWatchPage';
import LectureClipsPage from './pages/LectureClipsPage';
import BottomNavigation from './components/BottomNavigation';
import './styles/global.css';
import './styles/mobileVideoLayout.css';
import DataDebugger from './components/DataDebugger';
import { useConfig } from './config/ConfigContext';
import { LiveStatusProvider } from './contexts/LiveStatusContext';
import TestAuthPage from './pages/TestAuthPage';
import { initializePWATracking } from './utils/pwaInstallPrompt';
import { initializePushNotifications } from './services/pushNotifications';
import PWAInstallBanner from './components/PWAInstallBanner';
import LiveBanner from './components/LiveBanner';
import NotificationPrompt from './components/NotificationPrompt';
import { updateMetaTags } from './utils/metaTagUpdater';
import MetaTagsUpdater from './components/MetaTagsUpdater';
import { PlayerProvider } from './contexts/PlayerContext';
import GlobalVideoOverlay from './components/Video/GlobalVideoOverlay';
import MobileConsole from './components/Debug/MobileConsole';
import { hasAnyCommunityFeature, isCommunityFeatureEnabled } from './utils/featureHelpers';
import { EMBEDDED_APPS } from './config/embeddedApps';
import { SupabaseAuthProvider } from './providers/SupabaseAuthProvider';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import FeedbackPage from './pages/FeedbackPage';
import TaxDocsPage from './pages/TaxDocsPage';
import EmbeddedAppPage from './pages/EmbeddedAppPage';
import SearchLecturesPage from './pages/SearchLecturesPage';

/**
 * Main application component with routing
 * @returns {JSX.Element} App component
 */
function App() {
  // Get configuration from context (destructure properly)
  const { config, loading, error } = useConfig();
  
  // Initialize PWA tracking, push notifications, and update meta tags when ready
  useEffect(() => {
    if (config) {
      // PWA branding is now initialized in index.js before React renders
      // Initialize tracking for engagement metrics
      initializePWATracking(config);
      // Initialize push notifications if video options enabled
      if (config.video_options_enabled) {
        initializePushNotifications(config);
      }
      // Update meta tags with center configuration
      updateMetaTags(config);
    }
  }, [config]);
  
  // Add global error handler for YouTube iframe errors
  useEffect(() => {
    const handleError = (event) => {
      // Check if this is a YouTube iframe error
      if (event.error && event.error.message && 
          (event.error.message.includes('reading \'src\'') || 
           event.error.message.includes('YouTube') ||
           event.error.message.includes('iframe'))) {
        console.warn('YouTube iframe error caught:', event.error);
        // Prevent the error from crashing the app
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);
  
  // Detect mobile view following best practices
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  
  return (
    <SupabaseAuthProvider>
      <PlayerProvider>
        <LiveStatusProvider>
          <Router>
            <MetaTagsUpdater />
            <DataDebugger />
          {/* PWA Install Banner - shows based on engagement (mobile only) */}
          {config && isMobile && <PWAInstallBanner config={config} />}
          {/* Live Banner - shows when streams are live */}
          {config?.video_options_enabled && <LiveBanner />}
          {/* Notification prompt - shows after user engagement */}
          {config?.video_options_enabled && <NotificationPrompt />}
          <div 
            className="app"
            data-video-options={config?.video_options_enabled ? "true" : "false"}
            data-route={window.location.pathname}
          >
          <Routes>
            {/* Conditionally render homepage based on feature availability */}
            <Route path="/" element={
              (() => {
                if (!loading && config) {
                  // Check if videos are enabled
                  const hasVideos = config?.video_options_enabled === true;
                  
                  // Check if ANY community feature is actually enabled
                  // Using the centralized helper which already checks the master switch
                  const hasCommunity = hasAnyCommunityFeature(config);
                  
                  // Show RichHomePage if we have videos OR community features
                  // (in addition to lectures which are assumed to be always present)
                  if (hasVideos || hasCommunity) {
                    return <RichHomePage />;
                  }
                }
                // Default to lectures page (when only lectures, or still loading)
                return <NewHomePage />;
              })()
            } />
            
            {/* Dedicated lectures page route - always shows NewHomePage */}
            <Route path="/lectures" element={<NewHomePage />} />

            {/* Search lectures - semantic search across lecture embeddings */}
            <Route path="/lectures/search" element={<SearchLecturesPage />} />
            
            {/* Keep old homepage accessible via /classic route */}
            <Route path="/classic" element={<HomePage />} />
            
            {/* Events routes */}
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:eventName" element={<EventPage />} />
            
            {/* Series route - alias for events/:eventName */}
            <Route path="/series/:eventName" element={<EventPage />} />
            
            {/* New event page route */}
            <Route path="/event/:eventName" element={<EventPage />} />
            
            {/* Speaker page route */}
            <Route path="/speaker/:speakerName" element={<SpeakerPage />} />
            
            {/* Lecture route */}
            <Route path="/lecture/:lectureId" element={<LecturePage />} />
            
            {/* Publications routes - only available when center has publications_sheet_id configured */}
            <Route
              path="/publications"
              element={
                loading ? (
                  <PublicationsPage />
                ) : (
                  config?.publications_sheet_id ? <PublicationsPage /> : <Navigate replace to="/" />
                )
              }
            />
            <Route
              path="/publication/:publicationId"
              element={
                loading ? (
                  <PublicationsPage />
                ) : (
                  config?.publications_sheet_id ? <PublicationsPage /> : <Navigate replace to="/" />
                )
              }
            />
            
            {/* Shorts route - show optimistically while loading or if deep link */}
            <Route 
              path="/shorts" 
              element={
                loading || window.location.search.includes('v=') ? 
                  <ShortsViewer /> : 
                  (config?.video_options_enabled ? <ShortsViewer /> : <Navigate replace to="/" />)
              } 
            />
            
            {/* Videos routes - using same pattern as shorts for deep link support */}
            <Route 
              path="/videos" 
              element={
                loading ? 
                  <VideosPage /> : 
                  (config?.video_options_enabled ? <VideosPage /> : <Navigate replace to="/" />)
              } 
            />
            <Route 
              path="/videos/:videoId" 
              element={
                loading || window.location.pathname.match(/^\/videos\/[^\/]+$/) ? 
                  <VideoWatchPage /> : 
                  (config?.video_options_enabled ? <VideoWatchPage /> : <Navigate replace to="/" />)
              } 
            />
            
            {/* Lecture Clips route */}
            <Route
              path="/lecture-clips"
              element={
                loading || window.location.search.includes('v=') ?
                  <LectureClipsPage /> :
                  (config?.lecture_clips_enabled ? <LectureClipsPage /> : <Navigate replace to="/" />)
              }
            />

            {/* Community routes - conditionally rendered based on config */}
            {config?.community_features?.announcements && (
              <Route path="/community/announcements" element={<AnnouncementsPage />} />
            )}
            {/* Stripe return URL routes */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/thank-you" element={
              <Navigate 
                to={`/community/announcements${window.location.search}`} 
                replace 
              />
            } />
            {config?.community_features?.calendar && (
              <Route path="/community/calendar" element={<CalendarPage />} />
            )}
            {config?.community_features?.qa && (
              <Route path="/community/qa" element={<div style={{ padding: '50px', textAlign: 'center' }}>Q&A feature coming soon!</div>} />
            )}
            {config?.community_features?.services?.enabled && (
              <Route path="/community/services" element={<CommunityServicesPage />} />
            )}
            {config?.tax_receipts_sheet_id && (config?.community_features?.services?.enabled || config?.community_services_enabled) && (
              <Route path="/community/tax-docs" element={<TaxDocsPage />} />
            )}
            {/* Embedded external apps (Library, Housing, ...). One route per
                app the center has switched on — see config/embeddedApps.js.
                Filter before mapping so <Routes> only ever receives <Route>
                children, never `false`. */}
            {EMBEDDED_APPS
              .filter((app) => isCommunityFeatureEnabled(config, app.id))
              .map((app) => (
                <Route
                  key={app.id}
                  path={app.path}
                  element={<EmbeddedAppPage appId={app.id} />}
                />
              ))}
            
            {/* About route - defer redirect until config finished loading to avoid deep-link issues */}
            <Route
              path="/about"
              element={
                loading ? (
                  <AboutPage />
                ) : (
                  config?.about_us_enabled ? <AboutPage /> : <Navigate replace to="/" />
                )
              }
            />
            {/* Support route - defer redirect until config finished loading to avoid deep-link issues */}
            <Route
              path="/support"
              element={
                loading ? (
                  /* While config is still loading, optimistically show the SupportPage so deep links render */
                  <SupportPage />
                ) : (
                  config?.support_enabled ? <SupportPage /> : <Navigate replace to="/" />
                )
              }
            />
            
            {/* Authentication routes */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<EmailConfirmationPage />} />
            <Route path="/email-confirmed" element={<EmailConfirmationPage />} />

            {/* Feedback route - always available, no auth required */}
            <Route path="/feedback" element={<FeedbackPage />} />

            {/* Debug and Not Found routes */}
            <Route path="/debug" element={<DebugPage />} />
            <Route path="/test-auth" element={<TestAuthPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          
          {/* Bottom Navigation - shows when video options are enabled */}
          <BottomNavigation />
          {/* Render the persistent global video overlay once at root (must be inside Router for hooks) */}
          <GlobalVideoOverlay />
          {/* Mobile Debug Console - only show in development or with debug query param */}
          {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
            <MobileConsole />
          )}
        </div>
      </Router>
      </LiveStatusProvider>
    </PlayerProvider>
    </SupabaseAuthProvider>
  );
};

export default App;
